/**
 * Auswertung von Fehlerantworten des Backends (OEWR-91).
 *
 * Neuer Vertrag: Fehler kommen als ProblemDetails (`application/problem+json`):
 * `{ type, title, status, detail, traceId, code?, errors?, ... }`. `detail` enthält die
 * bisherige Meldung, `code` ist nur bei den fachlichen Fehlercodes (siehe `ErrorCodes`) gesetzt.
 *
 * Alter Vertrag: Fehler kommen als nackter String (die Meldung selbst).
 *
 * Beide Formate werden verstanden, solange Frontend und Backend nicht gleichzeitig ausgerollt sind.
 */

/** Fachliche Fehlercodes aus `Backend/Utils/Operations/ErrorCodes.cs` bzw. `/api/Auth/confirm`. */
export const ErrorCodes = {
	ComplaintAlreadyNotResponsible: 'ComplaintAlreadyNotResponsible',
	ComplaintAlreadyHasCase: 'ComplaintAlreadyHasCase',
	EmailAlreadyExists: 'EmailAlreadyExists',
	InvalidCodeFormat: 'InvalidCodeFormat',
	PasswordAlreadySet: 'PasswordAlreadySet',
	AlreadyConfirmed: 'AlreadyConfirmed',
	CsvImportValidationFailed: 'CsvImportValidationFailed',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

export interface ProblemDetails {
	type?: string | null;
	title?: string | null;
	status?: number | null;
	detail?: string | null;
	traceId?: string | null;
	code?: string | null;
	/** ASP.NET-Validierungsfehler: Feldname → Meldungen */
	errors?: Record<string, string[]> | null;
	[extension: string]: unknown;
}

export const isProblemDetails = (error: unknown): error is ProblemDetails => {
	return typeof error === 'object' && error !== null && !Array.isArray(error);
};

/** Liefert den fachlichen Fehlercode einer ProblemDetails-Antwort oder null (alter String-Fehler, kein Code). */
export const getErrorCode = (error: unknown): string | null => {
	if (isProblemDetails(error) && typeof error.code === 'string' && error.code.length > 0) {
		return error.code;
	}
	return null;
};

/**
 * Liefert eine anzeigbare Fehlermeldung: bei ProblemDetails `detail`, sonst die erste
 * Validierungsmeldung aus `errors`, sonst `title`; bei einem String-Fehler (alter Vertrag) den String.
 * Gibt nie ein Objekt als "[object Object]" aus — ohne verwertbaren Text kommt `fallback` zurück.
 */
export const getErrorMessage = (error: unknown, fallback: string): string => {
	if (typeof error === 'string') {
		return error.trim().length > 0 ? error : fallback;
	}

	if (isProblemDetails(error)) {
		if (typeof error.detail === 'string' && error.detail.length > 0) {
			return error.detail;
		}

		if (error.errors && typeof error.errors === 'object') {
			for (const messages of Object.values(error.errors)) {
				if (Array.isArray(messages) && typeof messages[0] === 'string' && messages[0].length > 0) {
					return messages[0];
				}
			}
		}

		if (typeof error.title === 'string' && error.title.length > 0) {
			return error.title;
		}
	}

	return fallback;
};
